/**
 * Fetches omnibus data from FiloBlu.
 *
 * @param {Object} params - Parameters for the request.
 * @param {string} [params.brandKey] - Omnibus brand key. Defaults to `window.dAgency.strings.omnibusBrandKey`.
 * @param {string} [params.sku] - SKU of the product. Defaults to an empty string.
 * @param {string} [params.currency] - Locale Currency. Defaults to an empty string.
 * @param {Object} [params.callbacks] - Optional callback functions.
 * @param {Function} [params.callbacks.afterFetch] - Function to call after successfully fetching the omnibus data.
 * @param {Function} [params.callbacks.errorCallback] - Function to call if an error occurs during the fetch.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the omnibus data object, or `false` if neither `brandKey` nor `sku` is provided.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
export const getOmnibus = async ({
  brandKey = window?.dAgency?.strings?.omnibusBrandKey,
  sku = '',
  currency = '',
  callbacks: { afterFetch, errorCallback } = {},
}) => {
  if (!brandKey && !sku) {
    console.error('Cannot fetch omnibus data, no brandKey or sku provided');
    return false;
  }

  const baseUrl = 'https://omnibus.filoblu.com/';
  const market = `${window.location.host}${window.Shopify.routes.root}`;
  const currencyValue = currency ? '&currency=' + currency : '';
  const omnibus = await fetch(`${baseUrl}?brand=${brandKey}&sku=${sku}&market=${market}${currencyValue}`)
    .then((res) => {
      if (!res.ok) {
        const err = new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
        if (typeof errorCallback !== 'undefined') errorCallback(err);
        console.error('Cart update failed:', err);
        throw err;
      }
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Cart update failed:', err);
      throw err;
    });
  if (typeof afterFetch !== 'undefined') await afterFetch(omnibus);
  return omnibus;
};
